import { useEffect, useState } from "react";
import {
  Button,
  cn,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@semo/shared-ui";
import { ShieldAlert } from "@semo/icons";

import { CONTRACT_RESTRICTION_QUESTIONS } from "./ContractRestrictionAllNoModal";

/**
 * paperCode 135 발주기관 최종 확인 모달.
 *
 * supplier 가 "예" 를 포함해 제출한 카드에서 buyer 가 [확인하기] 를 누르면 열린다.
 * 공급사가 "예"로 응답한 문항 문구를 그대로 노출하고, 최종 확인 응답(예/아니오)을 선택 후
 * [제출] 하면 백엔드가 c-market POST 를 태워 서류가 발급된다. 1회 확정 정책.
 */

export type BuyerConfirmationValue = "YES" | "NO";

interface ContractRestrictionBuyerConfirmModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** supplier 가 저장한 8개 응답. YES 항목의 문구를 되짚는 데 사용. */
  answers: readonly string[];
  /** YES 로 응답한 문항 인덱스. 서버 스냅샷의 flaggedIndexes 를 그대로 전달. */
  flaggedIndexes: readonly number[];
  onSubmit: (buyerConfirmation: BuyerConfirmationValue) => Promise<void>;
}

export function ContractRestrictionBuyerConfirmModal({
  open,
  onOpenChange,
  answers: _answers, // 상세 노출은 flaggedIndexes 기반이라 answers 자체는 참고용
  flaggedIndexes,
  onSubmit,
}: ContractRestrictionBuyerConfirmModalProps) {
  const [selection, setSelection] = useState<BuyerConfirmationValue | null>(
    null,
  );
  const [isSubmitting, setIsSubmitting] = useState(false);

  // 모달이 닫힐 때 로컬 선택을 초기화. 재오픈 시 이전 선택이 그대로 남아있지 않도록.
  useEffect(() => {
    if (!open) {
      setSelection(null);
    }
  }, [open]);

  const flaggedQuestions = flaggedIndexes.map(
    (index) =>
      CONTRACT_RESTRICTION_QUESTIONS[index] ?? `${String(index + 1)}번 문항`,
  );

  const canSubmit = selection !== null && !isSubmitting;

  const handleSubmit = async () => {
    if (!canSubmit || selection === null) return;
    setIsSubmitting(true);
    try {
      await onSubmit(selection);
      onOpenChange(false);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg max-h-[90vh] flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ShieldAlert className="w-4 h-4 text-amber-500" />
            수의계약 체결 여부 최종 확인
          </DialogTitle>
          <DialogDescription>
            공급사가 아래 항목에 <b>&quot;예&quot;</b>로 응답했습니다. 내용을
            검토하신 뒤 계약 진행 여부를 선택해 제출해 주세요. 제출 후에는 수정할
            수 없습니다.
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto space-y-4 pr-1">
          <section className="rounded-lg border border-amber-300 bg-amber-50 px-3 py-3">
            <h4 className="text-xs font-semibold text-amber-900">
              &quot;예&quot;로 응답한 항목 ({flaggedQuestions.length}건)
            </h4>
            <ul className="mt-2 space-y-1">
              {flaggedQuestions.map((question, idx) => (
                <li
                  key={flaggedIndexes[idx]}
                  className="text-xs leading-relaxed text-amber-900 pl-3 relative before:content-['·'] before:absolute before:left-0.5"
                >
                  {question}
                </li>
              ))}
            </ul>
          </section>

          <section>
            <h4 className="text-xs font-semibold text-slate-700 mb-2">
              최종 확인 응답
            </h4>
            <div className="flex flex-col gap-2">
              <ChoiceOption
                selected={selection === "YES"}
                onClick={() => setSelection("YES")}
                label="예 — 위 내용을 확인했으며 계약을 진행합니다"
              />
              <ChoiceOption
                selected={selection === "NO"}
                onClick={() => setSelection("NO")}
                label="아니오 — 계약을 진행하지 않습니다"
              />
            </div>
          </section>
        </div>

        <DialogFooter className="gap-2 pt-3">
          <Button
            type="button"
            variant="outline"
            disabled={isSubmitting}
            onClick={() => onOpenChange(false)}
          >
            취소
          </Button>
          <Button
            type="button"
            disabled={!canSubmit}
            onClick={() => {
              void handleSubmit();
            }}
          >
            {isSubmitting ? "제출 중..." : "제출"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ChoiceOption({
  selected,
  onClick,
  label,
}: {
  selected: boolean;
  onClick: () => void;
  label: string;
}) {
  return (
    <label
      className={cn(
        "flex items-start gap-2 rounded-lg border px-3 py-2.5 cursor-pointer transition-colors",
        selected
          ? "border-primary bg-primary/5"
          : "border-slate-200 bg-white hover:bg-slate-50",
      )}
    >
      <input
        type="radio"
        checked={selected}
        onChange={onClick}
        className="mt-0.5 w-4 h-4 text-primary-text focus:ring-primary/60"
      />
      <span className="text-sm leading-relaxed text-slate-800">{label}</span>
    </label>
  );
}
